import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
} from "reactstrap";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import PageLoader from "components/common/PageLoader";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import smtpSettingsSchema from "./schemas/smtp.schema";
import { getCompany, getSmtp, updateSmtp } from "./services/SettingsService";
import type { SmtpFormValues, SmtpSettingRow } from "./types/settings.types";
import { smtpToFormValues } from "./utils/settingsNormalize";
import { toastSettingsError } from "./utils/settingsToast";

const SmtpTab = () => {
  const { can } = useAccess();
  const canUpdate = can(Permissions.SETTINGS.UPDATE);
  const [loading, setLoading] = useState(true);
  const [hasCompany, setHasCompany] = useState(false);
  const [smtpRow, setSmtpRow] = useState<SmtpSettingRow | null>(null);

  const formik = useFormik<SmtpFormValues>({
    enableReinitialize: true,
    initialValues: smtpToFormValues(smtpRow),
    validationSchema: smtpSettingsSchema,
    onSubmit: async (values) => {
      if (!canUpdate || !hasCompany) return;
      try {
        const portNum = Number(values.port);
        const body: Parameters<typeof updateSmtp>[0] = {
          host: values.host.trim(),
          fromEmail: values.fromEmail.trim(),
          port: Number.isFinite(portNum) ? portNum : 587,
          secure: values.secure,
          username: values.username.trim() || null,
          fromName: values.fromName.trim() || null,
        };
        if (values.clearStoredPassword) {
          body.password = null;
        } else if (values.password.trim() !== "") {
          body.password = values.password;
        }

        const res = await updateSmtp(body);
        if (res.success) {
          toast.success(res.message || "SMTP settings saved");
          const row = await getSmtp();
          setSmtpRow(row);
          formik.setFieldValue("password", "");
          formik.setFieldValue("clearStoredPassword", false);
        } else {
          toast.error(res.message || "Could not save SMTP settings");
        }
      } catch (err) {
        toastSettingsError(err, "Could not save SMTP settings");
      }
    },
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const company = await getCompany();
      const ok = company != null;
      setHasCompany(ok);
      if (ok) {
        const row = await getSmtp();
        setSmtpRow(row);
      } else {
        setSmtpRow(null);
      }
    } catch {
      toast.error("Could not load SMTP settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return <PageLoader message="Loading SMTP settings…" />;
  }

  const hasStoredSmtp = smtpRow != null;

  return (
    <Form onSubmit={formik.handleSubmit}>
      {!hasCompany ? (
        <Alert color="warning" className="mb-3">
          Save a <strong>company profile</strong> first. SMTP is stored against
          the primary company.
        </Alert>
      ) : (
        <p className="text-muted small mb-3">
          Outgoing mail. Leave <strong>SMTP password</strong> blank to keep the
          current value; check <strong>clear stored password</strong> to remove
          it.
        </p>
      )}
      <Row className="mb-3">
        <Col lg={3}>
          <Label htmlFor="smtp-host" className="form-label">
            Host <span className="text-danger">*</span>
          </Label>
        </Col>
        <Col lg={9}>
          <Input
            id="smtp-host"
            name="host"
            placeholder="e.g. smtp.gmail.com"
            value={formik.values.host}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={!!(formik.touched.host && formik.errors.host)}
            disabled={!canUpdate || !hasCompany}
          />
          {formik.touched.host && formik.errors.host ? (
            <FormFeedback type="invalid">{formik.errors.host}</FormFeedback>
          ) : null}
        </Col>
      </Row>
      <Row className="mb-3">
        <Col lg={3}>
          <Label htmlFor="smtp-port" className="form-label">
            Port
          </Label>
        </Col>
        <Col lg={9}>
          <Input
            id="smtp-port"
            name="port"
            type="number"
            placeholder="587"
            value={formik.values.port}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={!!(formik.touched.port && formik.errors.port)}
            disabled={!canUpdate || !hasCompany}
          />
          {formik.touched.port && formik.errors.port ? (
            <FormFeedback type="invalid">{formik.errors.port}</FormFeedback>
          ) : null}
          <p className="text-muted small mb-0 mt-1">Default 587 if empty.</p>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col lg={3}>
          <Label htmlFor="smtp-secure" className="form-label">
            Secure (TLS/SSL)
          </Label>
        </Col>
        <Col lg={9}>
          <div className="form-check">
            <Input
              id="smtp-secure"
              name="secure"
              type="checkbox"
              className="form-check-input"
              checked={formik.values.secure}
              onChange={formik.handleChange}
              disabled={!canUpdate || !hasCompany}
            />
            <Label check htmlFor="smtp-secure" className="form-check-label">
              Use secure connection (recommended)
            </Label>
          </div>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col lg={3}>
          <Label htmlFor="smtp-user" className="form-label">
            Username
          </Label>
        </Col>
        <Col lg={9}>
          <Input
            id="smtp-user"
            name="username"
            value={formik.values.username}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={!canUpdate || !hasCompany}
          />
        </Col>
      </Row>
      <Row className="mb-3">
        <Col lg={3}>
          <Label htmlFor="smtp-pass" className="form-label">
            SMTP password
          </Label>
        </Col>
        <Col lg={9}>
          <Input
            id="smtp-pass"
            name="password"
            type="password"
            autoComplete="new-password"
            placeholder={
              hasStoredSmtp
                ? "Leave blank to keep current password"
                : "SMTP password or app password"
            }
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={!canUpdate || !hasCompany}
          />
          <div className="form-check mt-2">
            <Input
              id="smtp-clear-pass"
              name="clearStoredPassword"
              type="checkbox"
              className="form-check-input"
              checked={formik.values.clearStoredPassword}
              onChange={formik.handleChange}
              disabled={!canUpdate || !hasCompany}
            />
            <Label check htmlFor="smtp-clear-pass" className="form-check-label">
              Clear stored password
            </Label>
          </div>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col lg={3}>
          <Label htmlFor="smtp-from-email" className="form-label">
            From email <span className="text-danger">*</span>
          </Label>
        </Col>
        <Col lg={9}>
          <Input
            id="smtp-from-email"
            name="fromEmail"
            type="email"
            value={formik.values.fromEmail}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={
              !!(formik.touched.fromEmail && formik.errors.fromEmail)
            }
            disabled={!canUpdate || !hasCompany}
          />
          {formik.touched.fromEmail && formik.errors.fromEmail ? (
            <FormFeedback type="invalid">
              {formik.errors.fromEmail}
            </FormFeedback>
          ) : null}
        </Col>
      </Row>
      <Row className="mb-3">
        <Col lg={3}>
          <Label htmlFor="smtp-from-name" className="form-label">
            From name
          </Label>
        </Col>
        <Col lg={9}>
          <Input
            id="smtp-from-name"
            name="fromName"
            value={formik.values.fromName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={!canUpdate || !hasCompany}
          />
        </Col>
      </Row>
      {!canUpdate ? (
        <Alert color="light" className="border mb-3">
          You do not have permission to update settings.
        </Alert>
      ) : null}
      <div className="hstack gap-2 justify-content-end">
        <button
          type="submit"
          className="btn btn-primary"
          disabled={!canUpdate || !hasCompany || formik.isSubmitting}
        >
          {formik.isSubmitting ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-1"
                role="status"
                aria-hidden="true"
              />
              Saving…
            </>
          ) : (
            "Save SMTP"
          )}
        </button>
      </div>
    </Form>
  );
};

export default SmtpTab;
